import { ProgressBar } from '@intelie/ui'
import { useRequest } from 'ahooks'
import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { RoundedModal } from 'plugin-codaf-risk-ext-bowtie/generic'
import { associateBowtieData, getImportBowtieStatus } from 'plugin-codaf-risk-ext-bowtie/services'

import { IImportStatus, StatusStage } from '../../BowtieList'
import { ImportBody, ImportFooter, ProgressBarContentContainer, StyledProgressBar, TextContainer } from './styles'

interface IProps {
    show: boolean
    handleClose: (k: boolean) => void
    files: FileList
    setFiles: (k: FileList) => void
    isImporting: boolean
    setIsImporting: (k: boolean) => void
    handleGetBowtiesList: () => Promise<void>
    importStatus: IImportStatus
    setImportStatus: (k: IImportStatus) => void
}

export const ImportBowtieModal: React.FC<IProps> = ({
    files,
    handleClose,
    setFiles,
    show,
    isImporting,
    setIsImporting,
    handleGetBowtiesList,
    importStatus,
    setImportStatus
}) => {
    const openImportGuidePDF = (): void => {
        window.open('/content/plugin-codaf-risk-ext-bowtie/BowtieImportGuide.pdf')
    }

    const openBowtieImportTemplatePDF = (): void => {
        window.open('/content/plugin-codaf-risk-ext-bowtie/BowtieImportTemplate.xlsx')
    }

    const { cancel, run, data, error } = useRequest(getImportBowtieStatus, { manual: true, pollingInterval: 2000 })

    const submitImportData = async (file): Promise<void> => {
        try {
            if (file == null) {
                return globalNotification.error(i18n('Please select a file.'))
            }
            setIsImporting(true)

            await associateBowtieData(file)

            setImportStatus({ stage: StatusStage.START })
            run()
        } catch (error) {
            // globalNotification.error(error.message ?? i18n('An error occurred on importing Bowtie data.'))
            setImportStatus({
                stage: StatusStage.ERROR,
                errorMessage: error.message ?? i18n('An error occurred on importing Bowtie data.')
            })
            console.log(error)
        } finally {
            setFiles(null)
        }
    }

    useEffect(() => {
        if (data && data.message) {
            if (data.message === 'STARTING') {
                setImportStatus({ stage: StatusStage.START, statusDisplay: 'Starting...' })
            } else if (data.message === 'FINISHED') {
                setImportStatus({ stage: StatusStage.END, statusDisplay: 'Finished.' })
            } else if (data.message.startsWith('Exception caught:')) {
                setImportStatus({
                    stage: StatusStage.ERROR,
                    errorMessage: data.message.replace('Exception caught:', '').trim()
                })
            } else {
                const [stage, currentTotal] = data.message.split(':') as [StatusStage, string]
                const [current, total] = currentTotal.split('/').map(Number)

                const status = {
                    [StatusStage.BARRIER]: 'barrier',
                    [StatusStage.RD]: 'risk diagram',
                    [StatusStage.BOWTIE]: 'bowtie'
                }

                setImportStatus({ stage: stage, current, total, statusDisplay: status[stage] ?? stage })
            }
        }
    }, [data])

    useEffect(() => {
        if (error) {
            globalNotification.error(error.message ?? i18n('Unable to get import status'))
        }
    }, [error])

    useEffect(() => {
        if (importStatus?.stage === StatusStage.END || importStatus?.stage === StatusStage.ERROR) {
            cancel()
            if (importStatus?.stage === StatusStage.END) {
                handleGetBowtiesList()
            }
        }
    }, [importStatus])

    return (
        <RoundedModal
            show={show}
            onHide={() => {
                handleClose(false)
            }}
            aria-labelledby="contained-modal-title"
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title">
                    <i className="fas fa-upload" /> {i18n('Import data')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <ImportBody>
                    {isImporting ? (
                        <ProgressBarContentContainer
                            color={
                                importStatus?.stage === StatusStage.END
                                    ? 'var(--green-700)'
                                    : importStatus?.stage === StatusStage.ERROR
                                    ? 'var(--red-500)'
                                    : 'var(--blue-500)'
                            }
                        >
                            <div className="pbar-first-row">{i18n('Your data is being uploaded')}</div>
                            <div className="pbar-bar-bundle">
                                <StyledProgressBar now={100} />
                                {importStatus?.stage === StatusStage.END ? (
                                    <i className="fas fa-check" />
                                ) : importStatus?.stage === StatusStage.ERROR ? (
                                    <i className="fas fa-times" />
                                ) : (
                                    <i className="fas fa-spinner fa-spin" />
                                )}
                                <div className="pbar-counter">
                                    {importStatus?.stage === StatusStage.START ? (
                                        <>0/3</>
                                    ) : importStatus?.stage === StatusStage.RD ? (
                                        <>1/3</>
                                    ) : importStatus?.stage === StatusStage.BARRIER ? (
                                        <>2/3</>
                                    ) : importStatus?.stage === StatusStage.BOWTIE ? (
                                        <>3/3</>
                                    ) : (
                                        <></>
                                    )}
                                    {importStatus?.stage === StatusStage.END
                                        ? i18n('Complete')
                                        : importStatus?.stage === StatusStage.ERROR
                                        ? i18n('Error')
                                        : i18n('steps')}
                                </div>
                            </div>

                            <div className="pbar-entities-loaded">
                                <i className="far fa-info-circle" />
                                {importStatus?.stage === StatusStage.START
                                    ? i18n(importStatus?.statusDisplay)
                                    : importStatus?.stage === StatusStage.END
                                    ? i18n(importStatus?.statusDisplay)
                                    : importStatus?.stage === StatusStage.ERROR
                                    ? i18n(importStatus?.errorMessage)
                                    : i18n(
                                          `${importStatus?.current} of ${importStatus?.total} ${importStatus?.statusDisplay} loaded.`
                                      )}
                            </div>
                            <div className="pbar-last-row">
                                {importStatus?.stage === StatusStage.END
                                    ? i18n('Data successfully uploaded.')
                                    : importStatus?.stage === StatusStage.ERROR
                                    ? i18n('Fix the error in your import sheet and try again.')
                                    : i18n(
                                          'Please wait while we load your file. Do not close the page or exit the system during the process'
                                      )}
                            </div>
                        </ProgressBarContentContainer>
                    ) : files ? (
                        <>{`${i18n('Are you sure you want to upload')} "${files[0].name}"?`}</>
                    ) : (
                        <TextContainer>
                            <div>{i18n('You are about to import bowtie data.')}</div>
                            <div>
                                {i18n(
                                    'Please consider that importing data is a process that needs to be done thoroughlly or it will result in an error. '
                                )}
                                {i18n('We advise you to read the ')}
                                <a onClick={openImportGuidePDF}>{i18n('Import Guide')}</a>
                                {i18n(' and use the ')}
                                <a onClick={openBowtieImportTemplatePDF}>{i18n('Bowtie Import Template')}</a>.
                            </div>
                        </TextContainer>
                    )}
                </ImportBody>
            </Modal.Body>
            {!isImporting && (
                <ImportFooter>
                    {files ? (
                        <Button
                            className="no-outline-button"
                            bsStyle="primary"
                            type="button"
                            onClick={() => submitImportData(files[0])}
                        >
                            {i18n('Confirm import')}
                        </Button>
                    ) : (
                        <Button
                            className={`no-outline-button ${files ? '' : 'no-pad'}`}
                            bsStyle="primary"
                            type="button"
                            onClick={() => submitImportData(files[0])}
                        >
                            <label className="control-label importLabel">
                                <i className="far fa-edit" />

                                {i18n(' Select .xlsx file')}
                                <input
                                    type="file"
                                    accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                                    className="file-input"
                                    onChange={e => setFiles(e.target.files)}
                                />
                            </label>
                        </Button>
                    )}

                    <Button
                        className="no-outline-button"
                        type="button"
                        onClick={() => {
                            handleClose(false)
                        }}
                    >
                        {i18n('Cancel')}
                    </Button>
                </ImportFooter>
            )}
        </RoundedModal>
    )
}
